import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { faChevronDoubleDown, faChevronDoubleUp } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import {
	hasLeadScoringContent,
	LeadScoringInline,
} from "@/components/LeadScoringPopover/LeadScoringPopover";
import { toScoreLabel } from "@/components/LeadScoringPopover/scoreTier";
import { OverviewTab } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/OverviewTab";
import { Organization, People } from "@/pages/Private/pages/Apollo/schema/get-people";

import { ContactProspectSourceContext } from "../schema/contact-detail";

interface ContactLeadOverviewTabProps {
	prospect: People;
	organization?: Organization;
	sourceContexts: ContactProspectSourceContext[];
	/** Rendered directly below the Summary (e.g. the "Also on these lists" placement block). */
	belowSummary?: ReactNode;
	/** The "Recent Signals & Activities" box — rendered between the Summary and {@link belowSummary}. */
	recentBox?: ReactNode;
}

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const hasDisplayValue = (value: unknown): boolean => {
	if (value === null || typeof value === "undefined") {
		return false;
	}

	if (typeof value === "string") {
		return value.trim().length > 0;
	}

	if (Array.isArray(value)) {
		return value.length > 0;
	}

	if (isRecord(value)) {
		return Object.keys(value).length > 0;
	}

	return true;
};

const formatUnknownValue = (value: unknown): string | null => {
	if (!hasDisplayValue(value)) {
		return null;
	}

	if (["string", "number", "boolean"].includes(typeof value)) {
		return String(value);
	}

	try {
		return JSON.stringify(value, null, 2);
	} catch {
		return null;
	}
};

const getStringField = (value: unknown, key: string): string | null => {
	if (!isRecord(value)) {
		return null;
	}

	const field = value[key];

	return typeof field === "string" && field ? field : null;
};

const getPayloadUrl = (payload: unknown): string | null =>
	[
		"contentUrl",
		"contentResponseUrl",
		"url",
		"linkedinUrl",
		"offerLink",
		"placeLink",
		"website",
	].reduce<string | null>((url, key) => url ?? getStringField(payload, key), null);

const InfoRow: FunctionComponent<{ label: string; value: unknown }> = ({ label, value }) => {
	const formattedValue = formatUnknownValue(value);

	if (!formattedValue) {
		return null;
	}

	return (
		<div className="flex flex-col gap-1 text-sm">
			<span className="font-semibold text-text">{label}</span>
			<span className="whitespace-pre-wrap break-words text-text-meta">{formattedValue}</span>
		</div>
	);
};

const SourcePayload: FunctionComponent<{ title: string; value: unknown }> = ({ title, value }) => {
	const formattedValue = formatUnknownValue(value);
	const url = getPayloadUrl(value);
	const { t } = useTranslation();

	if (!formattedValue) {
		return null;
	}

	return (
		<div className="rounded-lg border border-border-light bg-white p-3">
			<div className="mb-2 flex items-center justify-between gap-2">
				<h4 className="text-sm font-semibold text-text">{title}</h4>
				{url && (
					<a
						className="text-xs font-medium text-blue-action"
						href={url}
						rel="noreferrer"
						target="_blank"
					>
						{t("contacts.leadPanel.externalLink")}
					</a>
				)}
			</div>
			<pre className="whitespace-pre-wrap break-words text-xs text-text-meta">{formattedValue}</pre>
		</div>
	);
};

const SourceContextCard: FunctionComponent<{
	context: ContactProspectSourceContext;
	index: number;
}> = ({ context, index }) => {
	const { t } = useTranslation();
	const title =
		context.listName ||
		context.sourceName ||
		context.sourceType ||
		t("contacts.leadPanel.sourceContext", { index: index + 1 });

	return (
		<div className="flex flex-col gap-3 rounded-lg border border-border-light bg-white p-3">
			<div>
				<h4 className="text-sm font-semibold text-text">{title}</h4>
				<div className="mt-1 flex flex-col gap-1 text-xs text-text-meta">
					<InfoRow label={t("contacts.leadPanel.list")} value={context.listName} />
					<InfoRow label={t("contacts.leadPanel.sourceType")} value={context.sourceType} />
					<InfoRow label={t("contacts.leadPanel.sourceName")} value={context.sourceName} />
				</div>
			</div>

			<SourcePayload title={t("contacts.leadPanel.sources.liPost")} value={context.liPostResult} />
			<SourcePayload title={t("contacts.leadPanel.sources.liEvent")} value={context.liEventEntry} />
			<SourcePayload
				title={t("contacts.leadPanel.sources.liCompanyActivity")}
				value={context.liCompanyActivityEntryResult}
			/>
			<SourcePayload title={t("contacts.leadPanel.sources.offer")} value={context.offer} />
			<SourcePayload
				title={t("contacts.leadPanel.sources.mapBusiness")}
				value={context.mapBusiness}
			/>
			<SourcePayload title={t("contacts.leadPanel.customColumns")} value={context.customColumns} />
			<SourcePayload
				title={t("contacts.leadPanel.additionalInformation")}
				value={context.additionalInformation}
			/>

			{context.columns && context.columns.length > 0 && (
				<div className="flex flex-col gap-2 rounded-lg border border-border-light bg-white p-3">
					<h4 className="text-sm font-semibold text-text">{t("contacts.leadPanel.columns")}</h4>
					{context.columns.map((column) => (
						<div key={column.id} className="flex flex-col gap-1 border-t border-border-light pt-2">
							<InfoRow label={column.name} value={column.value} />
							<InfoRow
								label={t("contacts.leadPanel.answerExplanation")}
								value={column.answerExplanation}
							/>
							{column.sourceUrl && (
								<a
									className="text-xs font-medium text-blue-action"
									href={column.sourceUrl}
									rel="noreferrer"
									target="_blank"
								>
									{t("contacts.leadPanel.externalLink")}
								</a>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

const SourceContextsSection: FunctionComponent<{
	sourceContexts: ContactProspectSourceContext[];
}> = ({ sourceContexts }) => {
	const { t } = useTranslation();

	if (sourceContexts.length === 0) {
		return null;
	}

	return (
		<details
			className="flex group w-full flex-col rounded-lg border border-border-light bg-white p-4"
			open
		>
			<summary className="flex w-full cursor-pointer flex-row items-start justify-between gap-2 bg-white">
				<h3 className="text-sm font-semibold text-text">
					{t("contacts.leadPanel.sourceContexts", { count: sourceContexts.length })}
				</h3>
				<DisclosureToggle />
			</summary>
			<div className="mt-4 flex flex-col gap-3">
				{sourceContexts.map((context, index) => (
					<SourceContextCard key={context.listPersonId} context={context} index={index} />
				))}
			</div>
		</details>
	);
};

const DisclosureToggle: FunctionComponent = () => {
	const { t } = useTranslation();

	return (
		<div>
			<div className="flex flex-row items-center justify-center gap-2 text-sm group-open:hidden">
				<p>{t("list.show.showMore")}</p>
				<Icon className="h-3 w-3 cursor-pointer text-sm" icon={faChevronDoubleDown} />
			</div>
			<div className="hidden flex-row items-center justify-center gap-2 text-sm group-open:flex">
				<p>{t("list.show.showLess")}</p>
				<Icon className="h-3 w-3 cursor-pointer text-sm" icon={faChevronDoubleUp} />
			</div>
		</div>
	);
};

const LeadScoringReasoning: FunctionComponent<{ prospect: People }> = ({ prospect }) => {
	const { t } = useTranslation();
	const scoring = prospect.scorePersonaCategoryResult;

	if (!scoring || !hasLeadScoringContent(scoring)) {
		return null;
	}

	return (
		<details className="flex group w-full flex-col rounded-lg border border-border-light bg-white p-4">
			<summary className="flex w-full cursor-pointer flex-row items-start justify-between gap-2 bg-white">
				<h3 className="text-sm font-semibold text-text">
					{t("contacts.leadPanel.leadScoringReasoning")}
				</h3>
				<DisclosureToggle />
			</summary>
			<div className="mt-4">
				<LeadScoringInline scoreLabel={toScoreLabel(scoring.overallScore)} scoring={scoring} />
			</div>
		</details>
	);
};

export const ContactLeadOverviewTab: FunctionComponent<ContactLeadOverviewTabProps> = ({
	prospect,
	organization,
	sourceContexts,
	belowSummary,
	recentBox,
}) => {
	return (
		<div className="flex flex-col gap-3.5">
			<OverviewTab
				afterSummary={recentBox}
				belowSummary={belowSummary}
				hasPersons={!!prospect}
				organization={organization}
				primaryPerson={prospect}
			/>
			<SourceContextsSection sourceContexts={sourceContexts} />
			<LeadScoringReasoning prospect={prospect} />
		</div>
	);
};
